import type React from 'react';
import {
  type ChangeEventHandler,
  type FunctionComponent,
  type MouseEventHandler,
  useCallback,
  useRef,
} from 'react';

import classNames from 'classnames';

import { ariaToReactProps } from '@iconik-io/shared-lib/src/react/a11y/aria';

import VARIANTS from '../../constants/variants';
import type { AnyObject } from '../../types/common';
import { ui } from '../../utils/helpers';
import IconButton from '../icon_button/icon_button';

import styles from './input_date.module.css';

type Props = {
  canClear?: boolean;
  readOnly?: boolean;
  onClear?: MouseEventHandler;
  onClick?: MouseEventHandler;
  inputProps?: AnyObject;
  value?: string;
  onInputChange?: ChangeEventHandler<HTMLInputElement>;
  error?: boolean;
  isDynamicWidth?: boolean;
  withTimePicker?: boolean;
};

const CalendarTrigger: FunctionComponent<Props> = (props) => {
  const {
    canClear,
    readOnly,
    onClear,
    inputProps = {},
    value,
    onInputChange,
    error,
    onClick,
    isDynamicWidth = false,
    withTimePicker,
  } = props;

  const inputElem = useRef<HTMLInputElement>(null);
  const currentInputValue = inputElem?.current?.value;

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onClear?.(event);
    },
    [onClear],
  );

  const handleCalendarClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    },
    [],
  );

  const getWidth = () => {
    if (!isDynamicWidth) {
      return undefined;
    }

    if (currentInputValue == null || currentInputValue.length <= 10) {
      return undefined;
    }

    // if currentValue.length > 10 adapt size of the field
    // e.g. for the case with the range of dates
    return `${currentInputValue.length}ch`;
  };

  return (
    <div
      className={classNames(styles.root, {
        [styles['root--disabled']]: inputProps.disabled,
      })}
      onClick={onClick}
    >
      <input
        ref={inputElem}
        name={inputProps.name}
        disabled={inputProps.disabled}
        id={inputProps.id}
        readOnly={inputProps.readOnly}
        autoComplete={inputProps.autoComplete}
        onBlur={inputProps.onBlur}
        value={value || ''}
        onChange={onInputChange}
        className={classNames(styles.input, { [styles.input_error]: error })}
        {...ariaToReactProps(inputProps.aria)}
        {...ui('input')}
        {...ui({ 'input-name': inputProps.name })}
        style={{ width: getWidth() }}
      />
      <IconButton
        className={classNames(styles.clear_button, {
          [styles['clear_button--visible']]: canClear,
        })}
        variant={VARIANTS.DANGER}
        iconName="deletion_erase"
        {...ui('clear')}
        tabIndex={-1}
        aria-hidden={canClear ? undefined : true}
        onClick={handleClear}
        size={14}
      />
      {!readOnly && (
        <IconButton
          className={styles.calendar_button}
          iconName={withTimePicker ? 'calendar_datetime' : 'calendar'}
          {...ui('open')}
          onClick={handleCalendarClick}
        />
      )}
    </div>
  );
};

export default CalendarTrigger;
